import React, { useEffect, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SecurityIcon from "@mui/icons-material/Security";
import CloseIcon from "@mui/icons-material/Close";

import CertificateService from "../../../services/certificateServices";

import {
    retriveStateList,
    retriveDistrictList,
    retriveManufacturerList,
} from "../../../../src/helper/index";


const CertificateScopeManagement = () => {
    // =====================================================
    // DATA
    // =====================================================

    const [agents, setAgents] = useState([]);
    const [scopeList, setScopeList] = useState([]);

    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [manufacturerOptions, setManufacturerOptions] = useState([]);
    const [protocolOptions, setProtocolOptions] = useState([]);

    // =====================================================
    // DIALOG
    // =====================================================

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState("create");

    // =====================================================
    // FORM
    // =====================================================

    const [selectedAgentId, setSelectedAgentId] = useState("");

    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [protocolFormats, setProtocolFormats] = useState([]);

    const [statesUnrestricted, setStatesUnrestricted] =
        useState(true);

    const [districtsUnrestricted, setDistrictsUnrestricted] =
        useState(true);

    const [manufacturersUnrestricted, setManufacturersUnrestricted] =
        useState(true);

    const [protocolFormatsUnrestricted, setProtocolFormatsUnrestricted] =
        useState(true);

    // =====================================================
    // LOADING
    // =====================================================

    const [loadingAgents, setLoadingAgents] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [loadingScopeList, setLoadingScopeList] = useState(false);
    const [loadingScope, setLoadingScope] = useState(false);
    const [saving, setSaving] = useState(false);

    // =====================================================
    // SNACKBAR
    // =====================================================

    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const showMessage = (
        message,
        severity = "success"
    ) => {
        setSnackbar({
            open: true,
            severity,
            message,
        });
    };

    const closeSnackbar = () => {
        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    };

    // =====================================================
    // LOAD TEAM LIST
    // GET /api/certificate/team/list/
    //
    // This API already returns both:
    // certificateteamlead
    // certificateexecutive
    // =====================================================

    const loadAgents = async () => {
        try {
            setLoadingAgents(true);

            const response =
                await CertificateService.getCertificateTeamLeadList();

            const results =
                response?.data?.results || [];

            const validAgents = results.filter(
                (user) =>
                    user?.role === "certificateteamlead" ||
                    user?.role === "certificateexecutive"
            );

            setAgents(validAgents);
        } catch (error) {
            console.error(
                "Load certificate team list error:",
                error
            );

            showMessage(
                error?.response?.data?.error ||
                "Unable to load certificate team members.",
                "error"
            );
        } finally {
            setLoadingAgents(false);
        }
    };

    // =====================================================
    // LOAD MASTER OPTIONS
    // =====================================================

    const loadOptions = async () => {
        try {
            setLoadingOptions(true);

            const [
                stateList,
                districtList,
                manufacturerList,
                protocolResponse,
            ] = await Promise.all([
                retriveStateList(),
                retriveDistrictList(),
                retriveManufacturerList(),
                CertificateService.getCertificateProtocolFormats(),
            ]);

            // -----------------------------
            // STATES
            // -----------------------------

            setStateOptions(
                (stateList || []).filter(
                    (item) =>
                        item?.value !== "" &&
                        item?.value !== undefined
                )
            );

            // -----------------------------
// DISTRICTS
// -----------------------------

setDistrictOptions(
    (districtList || []).filter(
        (item) =>
            item?.value !== "" &&
            item?.value !== undefined &&
            item?.value !== null
    )
);

            // -----------------------------
            // MANUFACTURERS
            // -----------------------------

            setManufacturerOptions(
                (manufacturerList || []).filter(
                    (item) =>
                        item?.value !== "" &&
                        item?.value !== undefined
                )
            );

            // -----------------------------
            // PROTOCOL FORMATS
            // -----------------------------

            const formats =
                protocolResponse?.data?.formats || [];

            setProtocolOptions(
                formats.map((item) => ({
                    value: item.value,
                    label: item.label,
                }))
            );
        } catch (error) {
            console.error(
                "Load scope options error:",
                error
            );

            showMessage(
                error?.response?.data?.error ||
                "Unable to load scope options.",
                "error"
            );
        } finally {
            setLoadingOptions(false);
        }
    };

    // =====================================================
    // LOAD DISTRICTS BASED ON SELECTED STATES
    // =====================================================

    const loadDistrictsForStates = async (selectedStates) => {
        if (!selectedStates || selectedStates.length === 0) {
            setDistrictOptions([]);
            return;
        }

        try {
            const responses = await Promise.all(
                selectedStates.map((stateId) =>
                    retriveDistrictList({
                        state: stateId,
                    })
                )
            );

            const combinedDistricts = responses.flat();

            // Remove duplicate districts
            const uniqueDistricts = [
                ...new Map(
                    combinedDistricts
                        .filter(
                            (item) =>
                                item?.value !== "" &&
                                item?.value !== undefined &&
                                item?.value !== null
                        )
                        .map((item) => [
                            String(item.value),
                            item,
                        ])
                ).values(),
            ];

            setDistrictOptions(uniqueDistricts);
        } catch (error) {
            console.error(
                "Load districts for selected states error:",
                error
            );

            setDistrictOptions([]);

            showMessage(
                error?.response?.data?.error ||
                "Unable to load districts.",
                "error"
            );
        }
    };

    // =====================================================
    // LOAD SCOPE OVERVIEW
    // GET /api/certificate/executive/scope/list/
    // =====================================================

    const loadScopeList = async () => {
        try {
            setLoadingScopeList(true);

            const response =
                await CertificateService.getCertificateExecutiveScopeList();

            setScopeList(
                response?.data?.results || []
            );
        } catch (error) {
            console.error(
                "Load scope list error:",
                error
            );

            showMessage(
                error?.response?.data?.error ||
                "Unable to load scope overview.",
                "error"
            );
        } finally {
            setLoadingScopeList(false);
        }
    };

    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {
        loadAgents();
        loadOptions();
        loadScopeList();
    }, []);

    // =====================================================
    // WHEN STATE CHANGES, LOAD ONLY ITS DISTRICTS
    // =====================================================

    // useEffect(() => {
    //     if (
    //         statesUnrestricted ||
    //         !states.length
    //     ) {
    //         setDistrictOptions([]);
    //         return;
    //     }

    //     loadDistrictsForStates(states);
    // }, [
    //     states,
    //     statesUnrestricted,
    // ]);

    // =====================================================
    // RESET FORM
    // =====================================================

    const resetForm = () => {
        setSelectedAgentId("");

        setStates([]);
        setDistricts([]);
        setManufacturers([]);
        setProtocolFormats([]);

        setStatesUnrestricted(true);
        setDistrictsUnrestricted(true);
        setManufacturersUnrestricted(true);
        setProtocolFormatsUnrestricted(true);
    };

    // =====================================================
    // OPEN CREATE DIALOG
    // =====================================================

    const handleOpenCreate = async () => {
        resetForm();

        setDialogMode("create");
        setOpenDialog(true);

        // Make sure latest team list is available
        await loadAgents();
    };

    // =====================================================
    // CLOSE DIALOG
    // =====================================================

    const handleCloseDialog = () => {
        if (saving) {
            return;
        }

        setOpenDialog(false);
        resetForm();
    };

    // =====================================================
    // LOAD EXISTING SCOPE
    // =====================================================

    const loadScope = async (agentId) => {
        if (!agentId) {
            resetForm();
            return;
        }

        try {
            setLoadingScope(true);

            const response =
                await CertificateService.getCertificateExecutiveScope(
                    agentId
                );

            const data = response?.data || {};

            // -----------------------------
            // UNRESTRICTED FLAGS
            // -----------------------------

            setStatesUnrestricted(
                data?.states_unrestricted !== false
            );

            setDistrictsUnrestricted(
                data?.districts_unrestricted !== false
            );

            setManufacturersUnrestricted(
                data?.manufacturers_unrestricted !== false
            );

            setProtocolFormatsUnrestricted(
                data?.protocol_formats_unrestricted !== false
            );

            // -----------------------------
            // STATES
            // -----------------------------

            setStates(
                (data?.states || []).map(
                    (item) => Number(item.id)
                )
            );

            // -----------------------------
            // DISTRICTS
            // -----------------------------

            setDistricts(
                (data?.districts || []).map(
                    (item) => Number(item.id)
                )
            );

            // -----------------------------
            // MANUFACTURERS
            // -----------------------------

            setManufacturers(
                (data?.manufacturers || []).map(
                    (item) => Number(item.id)
                )
            );

            // -----------------------------
            // PROTOCOLS
            // -----------------------------

            setProtocolFormats(
                data?.protocol_formats || []
            );
        } catch (error) {
            console.error(
                "Get certificate scope error:",
                error
            );

            showMessage(
                error?.response?.data?.error ||
                "Unable to load agent scope.",
                "error"
            );

            resetForm();
        } finally {
            setLoadingScope(false);
        }
    };

    // =====================================================
    // AGENT CHANGE
    // =====================================================

    const handleAgentChange = async (event) => {
        const agentId = event.target.value;

        setSelectedAgentId(agentId);

        // Existing scope will be loaded automatically.
        await loadScope(agentId);
    };

    // =====================================================
    // STATES CHANGE
    // =====================================================

    const handleStatesChange = (event) => {
    const value = event.target.value;

    const selectedStates =
        typeof value === "string"
            ? value
                .split(",")
                .filter(Boolean)
                .map(Number)
            : value.map(Number);

    setStates(selectedStates);
};

    // =====================================================
    // DISTRICTS CHANGE
    // =====================================================

    const handleDistrictsChange = (event) => {
        const value = event.target.value;

        setDistricts(
            typeof value === "string"
                ? value
                    .split(",")
                    .filter(Boolean)
                    .map(Number)
                : value
        );
    };

    // =====================================================
    // MANUFACTURERS CHANGE
    // =====================================================

    const handleManufacturersChange = (event) => {
        const value = event.target.value;

        setManufacturers(
            typeof value === "string"
                ? value
                    .split(",")
                    .filter(Boolean)
                    .map(Number)
                : value
        );
    };

    // =====================================================
    // PROTOCOL CHANGE
    // =====================================================

    const handleProtocolChange = (event) => {
        const value = event.target.value;

        setProtocolFormats(
            typeof value === "string"
                ? value
                    .split(",")
                    .filter(Boolean)
                : value
        );
    };

    // =====================================================
    // SAVE / SET SCOPE
    // =====================================================

    const handleSaveScope = async () => {
        if (!selectedAgentId) {
            showMessage(
                "Please select a certificate team member.",
                "error"
            );
            return;
        }

        try {
            setSaving(true);

            const payload = {
                agent_id: Number(selectedAgentId),

                states: statesUnrestricted
                    ? "all"
                    : states,

                districts: districtsUnrestricted
                    ? "all"
                    : districts,

                manufacturers: manufacturersUnrestricted
                    ? "all"
                    : manufacturers,

                protocol_formats:
                    protocolFormatsUnrestricted
                        ? "all"
                        : protocolFormats,
            };

            console.log(
                "Certificate Scope Payload:",
                payload
            );

            const response =
                await CertificateService.setCertificateExecutiveScope(
                    payload
                );

            showMessage(
                response?.data?.message ||
                "Access scope updated successfully.",
                "success"
            );

            // Close dialog
            setOpenDialog(false);

            // Reset
            resetForm();

            // Refresh overview
            await loadScopeList();
        } catch (error) {
            console.error(
                "Set certificate scope error:",
                error
            );

            const errorData =
                error?.response?.data;

            let message =
                "Failed to update access scope.";

            if (errorData?.error) {
                message = errorData.error;
            } else if (errorData?.errors) {
                message = Object.entries(
                    errorData.errors
                )
                    .map(
                        ([field, value]) =>
                            `${field}: ${Array.isArray(value)
                                ? value.join(", ")
                                : value
                            }`
                    )
                    .join(" | ");
            }

            showMessage(
                message,
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    // =====================================================
    // EDIT SCOPE
    // =====================================================

    const handleEditScope = async (scope) => {
        const agentId =
            scope?.agent?.id;

        if (!agentId) {
            showMessage(
                "Agent information is missing from scope.",
                "error"
            );
            return;
        }

        setDialogMode("edit");
        setOpenDialog(true);
        setSelectedAgentId(agentId);

        await loadScope(agentId);
    };

    // =====================================================
    // SCOPE DISPLAY HELPERS
    // =====================================================

    const getStateName = (item) => {
        return (
            item?.state ||
            item?.label ||
            item?.id ||
            "-"
        );
    };

    const getDistrictName = (item) => {
        return (
            item?.district ||
            item?.label ||
            item?.id ||
            "-"
        );
    };

    const getManufacturerName = (item) => {
        return (
            item?.company_name ||
            item?.label ||
            item?.id ||
            "-"
        );
    };

    const getProtocolName = (value) => {
        const option =
            protocolOptions.find(
                (item) =>
                    item.value === value
            );

        return (
            option?.label ||
            value ||
            "-"
        );
    };

    const renderScopeValue = (
        unrestricted,
        values,
        formatter
    ) => {
        if (unrestricted) {
            return (
                <Chip
                    label="All"
                    size="small"
                    color="success"
                    variant="outlined"
                />
            );
        }

        if (!values?.length) {
            return (
                <Chip
                    label="None"
                    size="small"
                    color="default"
                    variant="outlined"
                />
            );
        }

        return (
            <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
            >
                {values
                    .slice(0, 3)
                    .map((item, index) => (
                        <Chip
                            key={
                                item?.id ||
                                item ||
                                index
                            }
                            label={formatter(item)}
                            size="small"
                        />
                    ))}

                {values.length > 3 && (
                    <Chip
                        label={`+${values.length - 3
                            } more`}
                        size="small"
                        variant="outlined"
                    />
                )}
            </Stack>
        );
    };

    // =====================================================
    // RENDER
    // =====================================================

    return (
        <Box sx={{ p: 3 }}>
            {/* =================================================
          PAGE HEADER
      ================================================= */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: {
                        xs: "flex-start",
                        sm: "center",
                    },
                    flexDirection: {
                        xs: "column",
                        sm: "row",
                    },
                    gap: 2,
                    mb: 3,
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        fontWeight={700}
                    >
                        Certificate Scope Management
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Manage access scope for certificate
                        team leads and executives.
                    </Typography>
                </Box>

                <Stack
                    direction="row"
                    spacing={1}
                >
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadScopeList}
                        disabled={loadingScopeList}
                    >
                        {loadingScopeList
                            ? "Refreshing..."
                            : "Refresh"}
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                    >
                        Create Set Scope
                    </Button>
                </Stack>
            </Box>

            {/* =================================================
          SCOPE OVERVIEW
      ================================================= */}

            <Card>
                <CardContent>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <SecurityIcon color="primary" />

                        <Typography
                            variant="h6"
                            fontWeight={600}
                        >
                            Scope Overview
                        </Typography>
                    </Stack>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        Overview of configured certificate
                        access scopes.
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    <TableContainer>
                        <Table
                            size="small"
                            sx={{
                                minWidth: 1000,
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <strong>Agent</strong>
                                    </TableCell>

                                    <TableCell>
                                        <strong>Role</strong>
                                    </TableCell>

                                    <TableCell>
                                        <strong>States</strong>
                                    </TableCell>

                                    <TableCell>
                                        <strong>Districts</strong>
                                    </TableCell>

                                    <TableCell>
                                        <strong>Manufacturers</strong>
                                    </TableCell>

                                    <TableCell>
                                        <strong>Protocol Formats</strong>
                                    </TableCell>

                                    <TableCell align="center">
                                        <strong>Action</strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loadingScopeList ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            align="center"
                                            sx={{ py: 5 }}
                                        >
                                            <CircularProgress
                                                size={28}
                                            />

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 1 }}
                                            >
                                                Loading scope overview...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : scopeList.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            align="center"
                                            sx={{ py: 5 }}
                                        >
                                            <Typography
                                                variant="body1"
                                                fontWeight={600}
                                            >
                                                No scope configured
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 0.5 }}
                                            >
                                                Click "Create Set Scope"
                                                to configure access.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    scopeList.map(
                                        (scope, index) => (
                                            <TableRow
                                                key={
                                                    scope?.id ||
                                                    scope?.agent?.id ||
                                                    index
                                                }
                                                hover
                                            >
                                                {/* AGENT */}

                                                <TableCell>
                                                    <Typography
                                                        fontWeight={600}
                                                    >
                                                        {
                                                            scope?.agent
                                                                ?.name
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {
                                                            scope?.agent
                                                                ?.email
                                                        }
                                                    </Typography>
                                                </TableCell>

                                                {/* ROLE */}

                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            scope
                                                                ?.agent
                                                                ?.role ||
                                                            "-"
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>

                                                {/* STATES */}

                                                <TableCell>
                                                    {renderScopeValue(
                                                        scope?.states_unrestricted,
                                                        scope?.states,
                                                        getStateName
                                                    )}
                                                </TableCell>

                                                {/* DISTRICTS */}

                                                <TableCell>
                                                    {renderScopeValue(
                                                        scope?.districts_unrestricted,
                                                        scope?.districts,
                                                        getDistrictName
                                                    )}
                                                </TableCell>

                                                {/* MANUFACTURERS */}

                                                <TableCell>
                                                    {renderScopeValue(
                                                        scope?.manufacturers_unrestricted,
                                                        scope?.manufacturers,
                                                        getManufacturerName
                                                    )}
                                                </TableCell>

                                                {/* PROTOCOL */}

                                                <TableCell>
                                                    {renderScopeValue(
                                                        scope?.protocol_formats_unrestricted,
                                                        scope?.protocol_formats,
                                                        getProtocolName
                                                    )}
                                                </TableCell>

                                                {/* ACTION */}

                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            handleEditScope(
                                                                scope
                                                            )
                                                        }
                                                        title="Edit Scope"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* =================================================
          CREATE / EDIT SCOPE DIALOG
      ================================================= */}

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pr: 1,
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <SecurityIcon color="primary" />

                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                            >
                                {dialogMode === "edit"
                                    ? "Edit Certificate Scope"
                                    : "Create Set Scope"}
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Configure certificate access
                                permissions.
                            </Typography>
                        </Box>
                    </Stack>

                    <IconButton
                        onClick={handleCloseDialog}
                        disabled={saving}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3 }}>
                    {/* =================================================
              AGENT
          ================================================= */}

                    <FormControl
                        fullWidth
                        size="small"
                        disabled={
                            loadingAgents ||
                            loadingScope ||
                            dialogMode === "edit"
                        }
                        sx={{ mb: 3 }}
                    >
                        <InputLabel>
                            Certificate Team Member
                        </InputLabel>

                        <Select
                            value={selectedAgentId}
                            label="Certificate Team Member"
                            onChange={handleAgentChange}
                        >
                            {agents.map((agent) => (
                                <MenuItem
                                    key={agent.id}
                                    value={agent.id}
                                >
                                    {agent.name}{" "}
                                    {agent.email
                                        ? `(${agent.email})`
                                        : ""}{" "}
                                    - {agent.role}
                                </MenuItem>
                            ))}
                        </Select>

                        {loadingAgents && (
                            <FormHelperText>
                                Loading team members...
                            </FormHelperText>
                        )}
                    </FormControl>

                    {/* =================================================
              LOADING EXISTING SCOPE
          ================================================= */}

                    {loadingScope ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                py: 5,
                            }}
                        >
                            <Stack
                                alignItems="center"
                                spacing={1}
                            >
                                <CircularProgress />

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Loading existing scope...
                                </Typography>
                            </Stack>
                        </Box>
                    ) : (
                        <Grid
                            container
                            spacing={2}
                        >
                            {/* =================================================
                  STATES
              ================================================= */}

                            <Grid
                                item
                                xs={12}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{ p: 2 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={
                                                    statesUnrestricted
                                                }
                                                onChange={(e) => {
                                                    const checked =
                                                        e.target.checked;

                                                    setStatesUnrestricted(
                                                        checked
                                                    );

                                                    if (checked) {
                                                        setStates([]);
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography fontWeight={600}>
                                                    All States
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Allow access to all
                                                    states
                                                </Typography>
                                            </Box>
                                        }
                                    />

                                    {!statesUnrestricted && (
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 2 }}
                                        >
                                            <InputLabel>
                                                Select States
                                            </InputLabel>

                                            <Select
                                                multiple
                                                value={states}
                                                label="Select States"
                                                onChange={
                                                    handleStatesChange
                                                }
                                                renderValue={(
                                                    selected
                                                ) => (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {selected.map(
                                                            (value) => {
                                                                const option =
                                                                    stateOptions.find(
                                                                        (item) =>
                                                                            Number(
                                                                                item.value
                                                                            ) ===
                                                                            Number(
                                                                                value
                                                                            )
                                                                    );

                                                                return (
                                                                    <Chip
                                                                        key={value}
                                                                        label={
                                                                            option?.label ||
                                                                            value
                                                                        }
                                                                        size="small"
                                                                    />
                                                                );
                                                            }
                                                        )}
                                                    </Box>
                                                )}
                                            >
                                                {stateOptions.map(
                                                    (option) => (
                                                        <MenuItem
                                                            key={
                                                                option.value
                                                            }
                                                            value={
                                                                Number(
                                                                    option.value
                                                                )
                                                            }
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Card>
                            </Grid>

                            {/* =================================================
                  DISTRICTS
              ================================================= */}

                            <Grid
                                item
                                xs={12}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{ p: 2 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={
                                                    districtsUnrestricted
                                                }
                                                onChange={(e) => {
                                                    const checked =
                                                        e.target.checked;

                                                    setDistrictsUnrestricted(
                                                        checked
                                                    );

                                                    if (checked) {
                                                        setDistricts([]);
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography fontWeight={600}>
                                                    All Districts
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Allow access to all
                                                    districts
                                                </Typography>
                                            </Box>
                                        }
                                    />

                                    {!districtsUnrestricted && (
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 2 }}
                                            // disabled={
                                            //     statesUnrestricted ||
                                            //     states.length === 0
                                            // }
                                        >
                                            <InputLabel>
                                                Select Districts
                                            </InputLabel>

                                            <Select
                                                multiple
                                                value={districts}
                                                label="Select Districts"
                                                onChange={
                                                    handleDistrictsChange
                                                }
                                                renderValue={(
                                                    selected
                                                ) => (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {selected.map(
                                                            (value) => {
                                                                const option =
                                                                    districtOptions.find(
                                                                        (item) =>
                                                                            Number(
                                                                                item.value
                                                                            ) ===
                                                                            Number(
                                                                                value
                                                                            )
                                                                    );

                                                                return (
                                                                    <Chip
                                                                        key={value}
                                                                        label={
                                                                            option?.label ||
                                                                            value
                                                                        }
                                                                        size="small"
                                                                    />
                                                                );
                                                            }
                                                        )}
                                                    </Box>
                                                )}
                                            >
                                                {districtOptions.map(
                                                    (option) => (
                                                        <MenuItem
                                                            key={
                                                                option.value
                                                            }
                                                            value={
                                                                Number(
                                                                    option.value
                                                                )
                                                            }
                                                        >
                                                            {option.label}

                                                            {option.district_code
                                                                ? ` (${option.district_code})`
                                                                : ""}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Card>
                            </Grid>

                            {/* =================================================
                  MANUFACTURERS
              ================================================= */}

                            <Grid
                                item
                                xs={12}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{ p: 2 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={
                                                    manufacturersUnrestricted
                                                }
                                                onChange={(e) => {
                                                    const checked =
                                                        e.target.checked;

                                                    setManufacturersUnrestricted(
                                                        checked
                                                    );

                                                    if (checked) {
                                                        setManufacturers([]);
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography fontWeight={600}>
                                                    All Manufacturers
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Allow access to all
                                                    manufacturers
                                                </Typography>
                                            </Box>
                                        }
                                    />

                                    {!manufacturersUnrestricted && (
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 2 }}
                                        >
                                            <InputLabel>
                                                Select Manufacturers
                                            </InputLabel>

                                            <Select
                                                multiple
                                                value={manufacturers}
                                                label="Select Manufacturers"
                                                onChange={
                                                    handleManufacturersChange
                                                }
                                                renderValue={(
                                                    selected
                                                ) => (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {selected.map(
                                                            (value) => {
                                                                const option =
                                                                    manufacturerOptions.find(
                                                                        (item) =>
                                                                            Number(
                                                                                item.value
                                                                            ) ===
                                                                            Number(
                                                                                value
                                                                            )
                                                                    );

                                                                return (
                                                                    <Chip
                                                                        key={value}
                                                                        label={
                                                                            option?.label ||
                                                                            value
                                                                        }
                                                                        size="small"
                                                                    />
                                                                );
                                                            }
                                                        )}
                                                    </Box>
                                                )}
                                            >
                                                {manufacturerOptions.map(
                                                    (option) => (
                                                        <MenuItem
                                                            key={
                                                                option.value
                                                            }
                                                            value={
                                                                Number(
                                                                    option.value
                                                                )
                                                            }
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Card>
                            </Grid>

                            {/* =================================================
                  PROTOCOL FORMATS
              ================================================= */}

                            <Grid
                                item
                                xs={12}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{ p: 2 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={
                                                    protocolFormatsUnrestricted
                                                }
                                                onChange={(e) => {
                                                    const checked =
                                                        e.target.checked;

                                                    setProtocolFormatsUnrestricted(
                                                        checked
                                                    );

                                                    if (checked) {
                                                        setProtocolFormats([]);
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography fontWeight={600}>
                                                    All Protocol Formats
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Allow access to all
                                                    protocol formats
                                                </Typography>
                                            </Box>
                                        }
                                    />

                                    {!protocolFormatsUnrestricted && (
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 2 }}
                                        >
                                            <InputLabel>
                                                Select Protocol Formats
                                            </InputLabel>

                                            <Select
                                                multiple
                                                value={protocolFormats}
                                                label="Select Protocol Formats"
                                                onChange={
                                                    handleProtocolChange
                                                }
                                                renderValue={(
                                                    selected
                                                ) => (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {selected.map(
                                                            (value) => (
                                                                <Chip
                                                                    key={value}
                                                                    label={getProtocolName(
                                                                        value
                                                                    )}
                                                                    size="small"
                                                                />
                                                            )
                                                        )}
                                                    </Box>
                                                )}
                                            >
                                                {protocolOptions.map(
                                                    (option) => (
                                                        <MenuItem
                                                            key={
                                                                option.value
                                                            }
                                                            value={
                                                                option.value
                                                            }
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>

                <Divider />

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCloseDialog}
                        disabled={saving}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={
                            saving ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            ) : (
                                <SaveIcon />
                            )
                        }
                        onClick={handleSaveScope}
                        disabled={
                            saving ||
                            loadingScope ||
                            !selectedAgentId
                        }
                    >
                        {saving
                            ? "Saving..."
                            : dialogMode === "edit"
                                ? "Update Scope"
                                : "Set Scope"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* =================================================
          SNACKBAR
      ================================================= */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={closeSnackbar}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CertificateScopeManagement;